import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useAuth } from "@/app/providers/useAuth";
import { propertyService } from "@/features/properties/model/propertyServices";
import type { PropertyApiResponse } from "@/features/properties/model/propertiesTypes";
import type { AddressSelectOption } from "./AddressSelectPill.types";

export interface UseAddressListViewModelResult {
  options: AddressSelectOption[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const buildPropertiesQueryParam = (params: {
  account_id?: string;
  device_id?: string;
  ip_address?: string;
}): string => {
  const sp = new URLSearchParams();
  if (params.account_id) sp.set("account_id", params.account_id);
  if (params.device_id) sp.set("device_id", params.device_id);
  if (params.ip_address) sp.set("ip_address", params.ip_address);
  return sp.toString();
};

const mapPropertiesToOptions = (properties: PropertyApiResponse[]): AddressSelectOption[] => {
  const mapped = properties
    .map((p) => ({
      id: p?.property_id || "",
      label: p?.address?.full_address || p?.address?.street || "",
    }))
    .filter((p): p is { id: string; label: string } =>
      Boolean(p.id && p.id.trim() && p.label && p.label.trim())
    )
    .map((p) => ({ value: p.id, label: p.label }));

  // Dedupe while preserving order
  const seen = new Set<string>();
  const deduped: AddressSelectOption[] = [];
  for (const opt of mapped) {
    if (seen.has(opt.value)) continue;
    seen.add(opt.value);
    deduped.push(opt);
  }

  return deduped;
};

/**
 * Address list ViewModel
 * Fetches the user's properties and adapts them to `AddressSelectPill` options.
 */
export const useAddressListViewModel = (): UseAddressListViewModelResult => {
  const { user, isAuthenticated } = useAuth();

  const accountId = user?.id ? String(user.id) : undefined;

  const queryParam = useMemo(() => {
    // Note: ip_address is intentionally omitted for now.
    return buildPropertiesQueryParam({
      account_id: accountId,
      // device_id: getDeviceId(),
    });
  }, [accountId]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [QUERY_KEYS.PROPERTIES, accountId],
    queryFn: () => propertyService.getAll(queryParam),
    enabled: isAuthenticated && !!accountId,
  });

  const options = useMemo(() => mapPropertiesToOptions(data || []), [data]);

  return {
    options,
    isLoading,
    error: (error as Error) || null,
    refetch,
  };
};
